"use client";

import { useEffect, useRef, useState } from "react";
import { LoadingSpinner } from "@/components/icons";
import {
  QUERY_URL_DEBOUNCE_MS,
  formStateSignature,
  replaceSearchUrl,
} from "./searchUrl";
import type {
  DynamicExperienceWrapperProps,
  ObservedFormStateChange,
  UclApi,
} from "./types";

export type {
  DynamicExperienceEnvironment,
  DynamicExperienceWrapperProps,
} from "./types";

const AFFILIATE_BUNDLE_NAME = "affiliate-bundle";
const AFFILIATE_ADAPTER_SCRIPT_ID = "affiliate-ucl-adapter-script";
const UCL_POLL_INTERVAL_MS = 50;
const UCL_TIMEOUT_MS = 15_000;

const AFFILIATE_ADAPTER_BOOTSTRAP = `
!function (u,c,l) {u._UCL=l;var z=c.createElement('script');z.async=0;
z.src='https://affiliate-bundle.api.highereducation.com/latest/UCL.adapter.js';
z.onload=function(){u.UCL&&u.UCL.setTenantId&&u.UCL.setTenantId('edx.org');};
c.head.appendChild(z);
}(window, document, {
'identity': '96713be3-cb4d-424e-866a-38c943339e31'
});
`;

type Status = "loading" | "ready" | "error";

const getAffiliateUcl = (): UclApi | undefined => {
  const ucl = window.UCL;
  return ucl?.mount && ucl._baseUrl?.includes(AFFILIATE_BUNDLE_NAME)
    ? ucl
    : undefined;
};

export const ensureAffiliateAdapter = (): void => {
  if (getAffiliateUcl()) return;
  if (document.getElementById(AFFILIATE_ADAPTER_SCRIPT_ID)) return;

  const script = document.createElement("script");
  script.id = AFFILIATE_ADAPTER_SCRIPT_ID;
  script.async = false;
  script.setAttribute("data-ot-ignore", "");
  script.className = "optanon-category-C0001";
  script.text = AFFILIATE_ADAPTER_BOOTSTRAP;
  document.head.appendChild(script);
};

const normalizeDcs = (
  dcs: Readonly<Record<string, string>> | undefined,
): ReadonlyArray<readonly [string, string]> =>
  Object.entries(dcs ?? {}).sort(([leftKey], [rightKey]) =>
    leftKey.localeCompare(rightKey),
  );

const formStateValue = (value: string | readonly string[]): string =>
  typeof value === "string" ? value : value.join(",");

const formStateRecord = (
  formState: ObservedFormStateChange["formState"],
): Record<string, string> => {
  const record: Record<string, string> = {};
  Object.entries(formState).forEach(([key, value]) => {
    record[key] = formStateValue(value);
  });
  return record;
};

const DynamicExperienceWrapper = ({
  dcs,
  environment = "int",
  forcedFlowId,
  initialFormState,
  urlFormState,
  observedFormStateKeys = [],
  onError,
  onReady,
  tagularSourceKey,
}: DynamicExperienceWrapperProps) => {
  const slotRef = useRef<HTMLDivElement>(null);
  const onErrorRef = useRef(onError);
  const onReadyRef = useRef(onReady);
  const [status, setStatus] = useState<Status>("loading");
  const dcsKey = JSON.stringify(normalizeDcs(dcs));
  const echoFormState = urlFormState ?? initialFormState ?? {};
  const formStateKey = formStateSignature(echoFormState);
  const mountedFormStateKeyRef = useRef(formStateKey);
  const emittedFormStateKeyRef = useRef<string | null>(null);
  const ignoreEchoRef = useRef(false);
  const [formStateGeneration, setFormStateGeneration] = useState(0);
  const latestFormStateRef = useRef<Record<string, string>>(
    initialFormState ?? {},
  );
  const queryTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const observedKeysRef = useRef(observedFormStateKeys);
  observedKeysRef.current = observedFormStateKeys;
  const mountFormStateRef = useRef(initialFormState);
  mountFormStateRef.current =
    formStateGeneration > 0 && urlFormState !== undefined
      ? urlFormState
      : initialFormState;

  useEffect(() => {
    onErrorRef.current = onError;
    onReadyRef.current = onReady;
  }, [onError, onReady]);

  useEffect(() => {
    if (formStateKey === mountedFormStateKeyRef.current) {
      ignoreEchoRef.current = false;
      return;
    }

    const isOwnUrlEcho =
      ignoreEchoRef.current && formStateKey === emittedFormStateKeyRef.current;
    ignoreEchoRef.current = false;
    mountedFormStateKeyRef.current = formStateKey;
    if (isOwnUrlEcho) return;

    setFormStateGeneration((generation) => generation + 1);
  }, [formStateKey]);

  const commitSearchUrl = (formState: Record<string, string>) => {
    emittedFormStateKeyRef.current = replaceSearchUrl(formState);
    ignoreEchoRef.current = true;
  };

  // Search is one step, so stepId is unused. A multi-step flow must not share
  // this writer or another step's state would land on the search URL.
  const handleFormStateChange = (change: ObservedFormStateChange) => {
    const next = formStateRecord(change.formState);
    const previous = latestFormStateRef.current;
    const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
    const queryChanged = (previous.query ?? "") !== (next.query ?? "");
    const otherChanged = [...keys].some(
      (key) => key !== "query" && (previous[key] ?? "") !== (next[key] ?? ""),
    );
    latestFormStateRef.current = next;

    if (!queryChanged || otherChanged) {
      if (queryTimerRef.current) clearTimeout(queryTimerRef.current);
      commitSearchUrl(next);
      return;
    }

    if (queryTimerRef.current) clearTimeout(queryTimerRef.current);
    queryTimerRef.current = setTimeout(
      () => commitSearchUrl(latestFormStateRef.current),
      QUERY_URL_DEBOUNCE_MS,
    );
  };
  const handleFormStateChangeRef = useRef(handleFormStateChange);
  handleFormStateChangeRef.current = handleFormStateChange;

  useEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;

    let mountedUcl: UclApi | undefined;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const handleError = (error: Error) => {
      if (cancelled) return;
      setStatus("error");
      onErrorRef.current?.(error);
    };

    // UCL mounts asynchronously and only registers the container once done, so an
    // unmount issued mid-mount (StrictMode, prop changes) is a no-op. A container
    // per run lets a stale mount finish off-DOM instead of stacking in the slot.
    const container = document.createElement("div");
    container.className = "w-full";
    slot.appendChild(container);

    const mountExperience = (ucl: UclApi) => {
      try {
        const normalizedDcs = Object.fromEntries(
          JSON.parse(dcsKey) as Array<[string, string]>,
        );

        ucl.mount("DynamicExperience", container, {
          placement: "edx-app",
          forcedFlowId,
          environment,
          ...(Object.keys(normalizedDcs).length ? { dcs: normalizedDcs } : {}),
          ...(Object.keys(mountFormStateRef.current ?? {}).length
            ? { initialFormState: mountFormStateRef.current }
            : {}),
          observedFormStateKeys: observedKeysRef.current,
          onFormStateChange: (change: ObservedFormStateChange) => {
            if (cancelled) return;
            handleFormStateChangeRef.current(change);
          },
          tenant: {
            tagularSourceKey,
            domain: "edx.org",
            displayName: "edX",
          },
          onReady: () => {
            if (cancelled) {
              ucl.unmount?.(container);
              return;
            }
            setStatus("ready");
            onReadyRef.current?.();
          },
          onError: (error: Error) => handleError(error),
        });
        mountedUcl = ucl;
      } catch (error) {
        handleError(
          error instanceof Error ? error : new Error("Unable to mount UCL"),
        );
      }
    };

    const pollForUcl = () => {
      const ucl = getAffiliateUcl();
      if (ucl) {
        if (timeoutTimer) clearTimeout(timeoutTimer);
        mountExperience(ucl);
        return;
      }
      pollTimer = setTimeout(pollForUcl, UCL_POLL_INTERVAL_MS);
    };

    setStatus("loading");
    latestFormStateRef.current = mountFormStateRef.current ?? {};

    if (!tagularSourceKey) {
      handleError(new Error("Missing Tagular source key"));
      return () => container.remove();
    }

    ensureAffiliateAdapter();

    timeoutTimer = setTimeout(() => {
      if (pollTimer) clearTimeout(pollTimer);
      handleError(new Error("Timed out waiting for affiliate-bundle UCL"));
    }, UCL_TIMEOUT_MS);
    pollForUcl();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (timeoutTimer) clearTimeout(timeoutTimer);
      if (queryTimerRef.current) clearTimeout(queryTimerRef.current);
      mountedUcl?.unmount?.(container);
      container.remove();
    };
  }, [
    dcsKey,
    environment,
    forcedFlowId,
    formStateGeneration,
    tagularSourceKey,
  ]);

  return (
    <div className="container px-0" data-testid="dynamic-experience-wrapper">
      {status === "loading" && (
        <div className="flex justify-center py-8" aria-busy="true">
          <div className="animate-spin">
            <LoadingSpinner />
          </div>
        </div>
      )}
      <div
        ref={slotRef}
        className={status === "loading" ? "hidden w-full" : "w-full"}
        aria-busy={status === "loading"}
      />
    </div>
  );
};

export default DynamicExperienceWrapper;
